import types from 'redux/actions/actionTypes';
import { PaginationOptions } from 'react-data-table-component';
import { axiosV2 } from 'interceptors/axios';

import { Partner } from 'types';

import { dispatchError } from 'utils/dispatchError';
import { decryptResponse } from 'utils/token';
import { getAppDomainHost } from 'utils/getAppDomainHost';

import { updateSocietyData } from 'modules/society/redux/updateSocietyData';

import {
  createPartnersBulk,
  CreatePartnersBulkBody,
} from 'modules/partners/api/createPartnersBulk';

import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { addAlert } from 'modules/_shared/redux/alertActions';

type UpdatePartnerProps = {
  partnerId: string;
  updatePartnerData: Partial<Partner> & {
    userId: string;
    society: string;
    societyId?: string;
  };
  showAlert?: boolean;
};

export const addPartner =
  (partner: Partial<Partner>) => async (dispatch: any) => {
    // TODO: Implement cleaning and validation of partner data before sending to the server
    try {
      const payload = { ...partner, domain: getAppDomainHost() };
      const { data } = await axiosV2.post('/partners', payload);
      const decodedData: any = await decryptResponse(data);

      dispatch({
        type: types.CREATE_PARTNER,
        partner: decodedData.newPartner,
      });

      updateSocietyData(decodedData, dispatch);

      return decodedData.newPartner;
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
      return dispatchError(error, dispatch);
    }
  };

type AddPartnersBulkOptions = {
  showAlert?: boolean;
};

export const addPartnersBulk =
  (
    body: CreatePartnersBulkBody,
    { showAlert = true }: AddPartnersBulkOptions = {}
  ) =>
  async (dispatch: any) => {
    try {
      const decodedData = await dispatch(createPartnersBulk(body));

      if (!decodedData) {
        return null;
      }

      decodedData.created?.forEach((partner: Partner) => {
        dispatch({
          type: types.CREATE_PARTNER,
          partner,
        });
      });

      if (decodedData.societyPartnersCount) {
        updateSocietyData(
          { societyPartnersCount: decodedData.societyPartnersCount },
          dispatch
        );
      }

      if (showAlert) {
        dispatch(addAlert(alertBodyTypes.SOCIETY_UPDATED));
      }

      return decodedData;
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
      return dispatchError(error, dispatch);
    }
  };

export const getPartners =
  (societyId: string, params: any = {}, source = 'list') =>
  async (dispatch: any) => {
    const query = { params: { societyId, ...params, source } };

    try {
      const { data } = await axiosV2.get('/partners', query);

      const decodedData = (await decryptResponse(data)) as {
        data: Partner[];
        pagination: PaginationOptions;
      };
      const partners = decodedData.data;

      dispatch({
        type: types.GET_PARTNERS,
        partners,
      });

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getPartner =
  (partnerId: string, params = {}) =>
  async (dispatch: any) => {
    const query = { params };

    try {
      const { data } = await axiosV2.get(`/partners/${partnerId}`, query);
      const decodedData = await decryptResponse(data);

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const updatePartner =
  ({ partnerId, updatePartnerData }: UpdatePartnerProps) =>
  async (dispatch: any) => {
    const body = { ...updatePartnerData, domain: getAppDomainHost() };

    const { data } = await axiosV2.put(`/partners/${partnerId}`, body);
    const decodedData = await decryptResponse(data);

    dispatch({
      type: types.UPDATE_PARTNER,
      partner: decodedData,
    });
  };

export const deletePartner = (partnerId: string) => async (dispatch: any) => {
  try {
    await axiosV2.delete(`/partners/${partnerId}`);

    dispatch({
      type: types.DELETE_PARTNER,
      partnerId,
    });
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
    return dispatchError(error, dispatch);
  }
};

type TogglePartnerEquityMarketAccessProps = {
  partnerIds: string[];
  enabled: boolean;
};

export const togglePartnerEquityMarketAccess =
  ({ partnerIds, enabled }: TogglePartnerEquityMarketAccessProps) =>
  async (dispatch: any) => {
    try {
      const { data } = await axiosV2.put('/partners/equityMarket/toggle', {
        partnerIds,
        enabled,
      });
      const decodedData = await decryptResponse(data);

      dispatch({
        type: types.TOGGLE_PARTNER_EQUITY_MARKET_ACCESS,
        partnerIds,
        enabled,
      });

      return decodedData;
    } catch (error) {
      return dispatchError(error, dispatch);
    }
  };
